import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useT } from '../i18n';
import { useSession } from '../context/Session';
import { Empty, Icon, Loading, Modal, Spinner, Switch, useConfirm, useToast } from '../components/ui';
import { BusyButton } from '../components/nutrition';
import { adminPromoCodes } from '../lib/cloud';
import AdminFeedback from './AdminFeedback';
import AdminLeaderboard from './AdminLeaderboard';
import AdminTrainers from './AdminTrainers';

// Admin panel: promo codes, feedback, the XP ranking and trainers. Only users with the `admin` custom claim reach this
// page (AdminRoute), and every call is checked again by the admin functions.

// Plans a new code can grant (each needs a RevenueCat entitlement, see
// GRANTABLE_PLANS in fitviz_cloud/functions/subscriptions.py). Older codes may
// still show limitless/super.
const PLANS = [['t1', 'premiumPlan']];
const LEGACY_PLANS = [['limitless', 'limitlessPlan'], ['super', 'superPlan']];

const ERRORS = {
  exists: 'adminErrExists',
  invalidCode: 'adminErrInvalidCode',
  invalidNumbers: 'adminErrInvalidNumbers',
  invalidDate: 'adminErrInvalidDate',
  invalidPlan: 'anErrorOccurred',
};

const EMPTY_FORM = { code: '', plan: 't1', duration_days: '30', max_uses: '10', expires_at: '', note: '' };

function useDate() {
  const t = useT();
  return (iso, withTime = false) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return withTime
      ? d.toLocaleString(t.locale, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
      : d.toLocaleDateString(t.locale, { day: 'numeric', month: 'short', year: 'numeric' });
  };
}

function planLabel(t, plan) {
  const found = [...PLANS, ...LEGACY_PLANS].find(([v]) => v === plan);
  return found ? t(found[1]) : t('adminLegacy');
}

function CreateCodeForm({ onCreated }) {
  const t = useT();
  const toast = useToast();
  const [f, setF] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const digits = (k) => (e) => setF({ ...f, [k]: e.target.value.replace(/[^0-9]/g, '') });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const res = await adminPromoCodes('create', {
        code: f.code.trim().toUpperCase(),
        plan: f.plan,
        duration_days: Number(f.duration_days),
        max_uses: Number(f.max_uses),
        // End of the chosen day in the admin's time zone.
        expires_at: f.expires_at ? new Date(`${f.expires_at}T23:59:59`).toISOString() : null,
        note: f.note,
      });
      if (res.code) {
        toast.success(`${t('adminCodeCreated')}: ${res.code.code}`);
        setF(EMPTY_FORM);
        onCreated(res.code);
      } else {
        setError(t(ERRORS[res.error] || 'anErrorOccurred'));
      }
    } catch (err) {
      setError(t('anErrorOccurred'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="gm-card gm-card--pad-lg gm-stack-sm" onSubmit={submit}>
      <div className="gm-upper-sm gm-muted">{t.upper(t('adminCreateCode'))}</div>
      <div className="gm-grid-2">
        <div className="gm-field">
          <label htmlFor="adm-code">{t('adminCode')}</label>
          <input
            id="adm-code"
            className="gm-input"
            style={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5 }}
            value={f.code}
            maxLength={32}
            autoComplete="off"
            spellCheck={false}
            placeholder="EMRE10"
            required
            onChange={(e) => setF({ ...f, code: e.target.value.replace(/[^a-zA-Z0-9_-]/g, '').toUpperCase() })}
          />
        </div>
        <div className="gm-field">
          <label htmlFor="adm-plan">{t('adminPlan')}</label>
          <select id="adm-plan" className="gm-select" value={f.plan} onChange={set('plan')}>
            {PLANS.map(([v, k]) => <option key={v} value={v}>{t(k)}</option>)}
          </select>
        </div>
        <div className="gm-field">
          <label htmlFor="adm-days">{t('adminDurationDays')}</label>
          <input id="adm-days" className="gm-input" inputMode="numeric" required value={f.duration_days} onChange={digits('duration_days')} />
        </div>
        <div className="gm-field">
          <label htmlFor="adm-uses">{t('adminMaxUses')}</label>
          <input id="adm-uses" className="gm-input" inputMode="numeric" required value={f.max_uses} onChange={digits('max_uses')} />
        </div>
        <div className="gm-field">
          <label htmlFor="adm-exp">{t('adminLastRedeemDate')}</label>
          <input id="adm-exp" className="gm-input" type="date" value={f.expires_at} onChange={set('expires_at')} />
        </div>
        <div className="gm-field">
          <label htmlFor="adm-note">{t('adminNote')}</label>
          <input id="adm-note" className="gm-input" maxLength={200} value={f.note} onChange={set('note')} />
        </div>
      </div>
      {error && <p className="gm-error-text gm-small">{error}</p>}
      <div className="gm-row" style={{ justifyContent: 'flex-end' }}>
        <BusyButton busy={busy} disabled={!f.code || !f.duration_days || !f.max_uses} className="gm-btn" type="submit" onClick={undefined}>
          <Icon name="add" size={18} /> {t('adminCreateCode')}
        </BusyButton>
      </div>
    </form>
  );
}

function RedemptionsModal({ code, onClose }) {
  const t = useT();
  const fmtDate = useDate();
  const [rows, setRows] = useState(null);
  useEffect(() => {
    let alive = true;
    adminPromoCodes('redemptions', { id: code.id })
      .then((res) => alive && setRows(res.redemptions || []))
      .catch(() => alive && setRows([]));
    return () => { alive = false; };
  }, [code.id]);
  return (
    <Modal open onClose={onClose} wide title={`${t('adminRedemptions')} · ${code.code}`}>
      {rows === null && <Loading />}
      {rows && rows.length === 0 && <Empty icon="person_off">{t('adminNoRedemptions')}</Empty>}
      {rows && rows.length > 0 && (
        <div className="gm-admin-table-wrap">
          <table className="gm-admin-table">
            <thead>
              <tr><th>{t('email')}</th><th>{t('adminRedeemedAt')}</th><th>{t('adminEndsAt')}</th></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.uid}>
                  <td>
                    <div className="gm-strong">{r.name || '—'}</div>
                    <div className="gm-small gm-muted">{r.email || r.uid}</div>
                  </td>
                  <td>{fmtDate(r.redeemed_at, true)}</td>
                  <td>{r.status === 'granted' ? fmtDate(r.ends_at) : <span className="gm-muted">{r.status}</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}

function CodeRow({ code, onToggle, onShow, onDelete }) {
  const t = useT();
  const toast = useToast();
  const fmtDate = useDate();
  const [busy, setBusy] = useState(false);
  const full = code.used_count >= code.max_uses;
  const expired = code.expires_at && new Date(code.expires_at) < new Date();
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code.code);
      toast.success(t('adminCopied'));
    } catch (e) {
      // clipboard blocked; nothing to do
    }
  };
  const toggle = async (v) => {
    setBusy(true);
    await onToggle(code, v);
    setBusy(false);
  };
  return (
    <tr className={!code.active || full || expired ? 'gm-admin-row--dim' : undefined}>
      <td>
        <button type="button" className="gm-admin-code" onClick={copy} title={t('adminCopied')}>
          {code.code} <Icon name="content_copy" size={14} />
        </button>
        {code.note && <div className="gm-small gm-muted">{code.note}</div>}
      </td>
      <td>
        {planLabel(t, code.plan)}
        <div className="gm-small gm-muted">{code.duration_days ? `${code.duration_days} ${t('adminDays')}` : '—'}</div>
      </td>
      <td>
        <button type="button" className="gm-link" onClick={() => onShow(code)}>{`${code.used_count} / ${code.max_uses}`}</button>
      </td>
      <td className={expired ? 'gm-error-text' : undefined}>{fmtDate(code.expires_at)}</td>
      <td>
        <div className="gm-row" style={{ gap: 8 }}>
          {busy ? <Spinner /> : <Switch checked={code.active} onChange={toggle} label={code.active ? t('adminActive') : t('adminInactive')} />}
          <button
            type="button"
            className="gm-icon-btn gm-icon-btn--sm"
            style={{ color: 'var(--gm-error)' }}
            onClick={() => onDelete(code)}
            disabled={busy}
            title={t('adminDelete')}
            aria-label={`${t('adminDelete')} ${code.code}`}
          >
            <Icon name="delete" size={20} />
          </button>
        </div>
      </td>
    </tr>
  );
}

function PromoCodes() {
  const t = useT();
  const toast = useToast();
  const confirm = useConfirm();
  const { refreshAdmin } = useSession();
  // Loads only on Refresh (also the first time): undefined = not loaded yet, null = loading.
  const [codes, setCodes] = useState(undefined);
  const [error, setError] = useState('');
  const [shown, setShown] = useState(null);

  const load = useCallback(async () => {
    setCodes(null);
    setError('');
    try {
      let res = await adminPromoCodes('list');
      if (res.error === 'forbidden') {
        // Claim may have been granted after this session's token was minted.
        if (await refreshAdmin(true)) res = await adminPromoCodes('list');
      }
      if (res.codes) setCodes(res.codes);
      else {
        setCodes([]);
        setError(res.error === 'forbidden' ? t('anErrorOccurred') : String(res.error || ''));
      }
    } catch (e) {
      setCodes([]);
      setError(t('anErrorOccurred'));
    }
  }, [refreshAdmin, t]);

  const toggle = async (code, active) => {
    const res = await adminPromoCodes('setActive', { id: code.id, active });
    if (res.ok) setCodes((list) => list.map((c) => (c.id === code.id ? { ...c, active } : c)));
    else toast.error(t('anErrorOccurred'));
  };

  const remove = async (code) => {
    const ok = await confirm({
      title: t('adminDeleteTitle'),
      message: `${code.code} — ${t('adminDeleteMessage')}`,
      confirmLabel: t('adminDelete'),
      danger: true,
    });
    if (!ok) return;
    const res = await adminPromoCodes('delete', { id: code.id });
    if (res.ok) {
      setCodes((list) => list.filter((c) => c.id !== code.id));
      toast.success(t('adminDeleted'));
    } else {
      toast.error(t('anErrorOccurred'));
    }
  };

  return (
    <>
      <div className="gm-stack">
        <div className="gm-row" style={{ justifyContent: 'space-between', gap: 8 }}>
          <p className="gm-small gm-muted">{t('adminRedeemHint')}</p>
          <button type="button" className="gm-btn gm-btn--soft gm-btn--sm" onClick={load}>
            <Icon name="refresh" size={18} /> {t('adminRefresh')}
          </button>
        </div>
        <CreateCodeForm onCreated={(c) => setCodes((list) => [c, ...(list || [])])} />
        <section className="gm-card gm-card--pad-lg">
          {codes === undefined && <Empty icon="refresh">{t('adminPressRefresh')}</Empty>}
          {codes === null && <Loading />}
          {error && <p className="gm-error-text gm-small">{error}</p>}
          {codes && codes.length === 0 && !error && <Empty icon="sell">{t('adminNoCodes')}</Empty>}
          {codes && codes.length > 0 && (
            <div className="gm-admin-table-wrap">
              <table className="gm-admin-table">
                <thead>
                  <tr>
                    <th>{t('adminCode')}</th>
                    <th>{t('adminPlan')}</th>
                    <th>{t('adminUses')}</th>
                    <th>{t('adminLastRedeemDate')}</th>
                    <th>{t('adminActive')}</th>
                  </tr>
                </thead>
                <tbody>
                  {codes.map((c) => <CodeRow key={c.id} code={c} onToggle={toggle} onShow={setShown} onDelete={remove} />)}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
      {shown && <RedemptionsModal code={shown} onClose={() => setShown(null)} />}
    </>
  );
}

export default function Admin() {
  const t = useT();
  const [params, setParams] = useSearchParams();
  const tab = ['feedback', 'xp', 'trainers'].includes(params.get('tab')) ? params.get('tab') : 'codes';
  const [newFeedback, setNewFeedback] = useState(0);
  return (
    <div className="gm-content gm-enter">
      <div className="gm-page-head">
        <h1 className="gm-page-title">{t.upper(t('adminPanel'))}</h1>
      </div>
      <div className="gm-segment gm-admin-tabs">
        <button type="button" className={tab === 'codes' ? 'active' : ''} onClick={() => setParams({}, { replace: true })}>
          {t.upper(t('adminPromoCodes'))}
        </button>
        <button type="button" className={tab === 'feedback' ? 'active' : ''} onClick={() => setParams({ tab: 'feedback' }, { replace: true })}>
          {t.upper(t('adminFeedback'))}{newFeedback > 0 ? ` (${newFeedback})` : ''}
        </button>
        <button type="button" className={tab === 'xp' ? 'active' : ''} onClick={() => setParams({ tab: 'xp' }, { replace: true })}>
          {t.upper(t('adminLeaderboard'))}
        </button>
        <button type="button" className={tab === 'trainers' ? 'active' : ''} onClick={() => setParams({ tab: 'trainers' }, { replace: true })}>
          {t.upper(t('adminTrainers'))}
        </button>
      </div>
      {tab === 'codes' && <PromoCodes />}
      {tab === 'feedback' && <AdminFeedback onNewCount={setNewFeedback} />}
      {tab === 'xp' && <AdminLeaderboard />}
      {tab === 'trainers' && <AdminTrainers />}
    </div>
  );
}
